import { useState, type Dispatch, type SetStateAction } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/ui';
import {
  validateEmail,
  validatePassword,
  validatePasswordMatch,
  validateTermsAccepted,
  validateUsResidencyConfirmed,
  validateTelegram,
  validateWhatsapp,
  buildRegistrationContactPayload,
  getFailedPasswordPolicyRules,
  isPasswordPolicyMet,
  type PasswordPolicyRule,
} from '@entities/auth';
import { useRegister } from '@features/auth/register';
import {
  RegistrationForm,
  type RegistrationBlurField,
  type RegistrationFormData,
} from '@features/auth/register/ui/registration-form';
import { AuthScreen } from '@widgets/auth/auth-screen';

interface RegisterPageProps {
  onNeedEmailVerification?: (email?: string) => void;
  onSwitchToLogin?: () => void;
}

type ValidatedField = RegistrationBlurField | 'termsAccepted' | 'usResidencyConfirmed';

const VALIDATED_FIELDS: ValidatedField[] = [
  'email',
  'password',
  'confirmPassword',
  'telegram',
  'whatsapp',
  'termsAccepted',
  'usResidencyConfirmed',
];

const POLICY_ERROR_KEY = 'recovery.policyLabel';

const initialFormData: RegistrationFormData = {
  email: '',
  password: '',
  confirmPassword: '',
  telegram: '',
  whatsapp: '',
  termsAccepted: false,
  usResidencyConfirmed: false,
  marketingAccepted: false,
};

export function RegisterPage({ onNeedEmailVerification, onSwitchToLogin }: RegisterPageProps) {
  const { t } = useTranslation('auth');
  const { toast } = useToast();
  const { register, isLoading } = useRegister();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') || undefined;

  const [formData, setFormData] = useState<RegistrationFormData>(initialFormData);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [policyRules, setPolicyRules] = useState<PasswordPolicyRule[]>([]);

  /** Ключ ошибки поля или undefined, если значение корректно. */
  const validateField = (field: ValidatedField, data: RegistrationFormData): string | undefined => {
    switch (field) {
      case 'email':
        return validateEmail(data.email).error;
      case 'telegram':
        return validateTelegram(data.telegram).error;
      case 'whatsapp':
        return validateWhatsapp(data.whatsapp).error;
      case 'password': {
        const result = validatePassword(data.password);
        if (!result.isValid) return result.error;
        return isPasswordPolicyMet(data.password) ? undefined : POLICY_ERROR_KEY;
      }
      case 'confirmPassword':
        return validatePasswordMatch(data.password, data.confirmPassword).error;
      case 'termsAccepted':
        return validateTermsAccepted(data.termsAccepted).error;
      case 'usResidencyConfirmed':
        return validateUsResidencyConfirmed(data.usResidencyConfirmed).error;
    }
  };

  const setFieldError = (field: ValidatedField, error: string | undefined) => {
    setErrors((prev) => {
      if (prev[field] === error) return prev;
      const next = { ...prev };
      if (error) next[field] = error;
      else delete next[field];
      return next;
    });
  };

  const handleFieldBlur = (field: RegistrationBlurField) => {
    const error = validateField(field, formData);
    setFieldError(field, error);
    if (field === 'password') {
      setPolicyRules(error === POLICY_ERROR_KEY ? getFailedPasswordPolicyRules(formData.password) : []);
      // Совпадение с подтверждением зависит от пароля, но показываем его только если поле уже трогали.
      if (formData.confirmPassword) setFieldError('confirmPassword', validateField('confirmPassword', formData));
    }
  };

  // Поле с ошибкой перепроверяется на каждом вводе: ошибка снимается сразу после исправления.
  const handleFormChange: Dispatch<SetStateAction<RegistrationFormData>> = (value) => {
    const next = typeof value === 'function' ? value(formData) : value;
    setFormData(next);
    VALIDATED_FIELDS.filter((field) => errors[field]).forEach((field) => {
      setFieldError(field, validateField(field, next));
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validations = {
      email: validateEmail(formData.email),
      telegram: validateTelegram(formData.telegram),
      whatsapp: validateWhatsapp(formData.whatsapp),
      password: validatePassword(formData.password),
      confirmPassword: validatePasswordMatch(formData.password, formData.confirmPassword),
      termsAccepted: validateTermsAccepted(formData.termsAccepted),
      usResidencyConfirmed: validateUsResidencyConfirmed(formData.usResidencyConfirmed),
    };

    const newErrors: Record<string, string> = {};
    Object.entries(validations).forEach(([key, validation]) => {
      if (!validation.isValid && validation.error) {
        newErrors[key] = validation.error;
      }
    });

    if (!isPasswordPolicyMet(formData.password)) {
      const failedRules = getFailedPasswordPolicyRules(formData.password);
      setPolicyRules(failedRules);
      newErrors.password = POLICY_ERROR_KEY;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (newErrors.password === POLICY_ERROR_KEY) {
        toast(t('errors.passwordPolicyNotMet'), 'error');
      }
      return;
    }

    setErrors({});
    setPolicyRules([]);

    const contacts = buildRegistrationContactPayload(formData.telegram, formData.whatsapp);

    const result = await register({
      email: formData.email,
      password: formData.password,
      repeatPassword: formData.confirmPassword,
      referralCode,
      ...contacts,
    });

    if (result.success) {
      if (result.action === 'confirm_email') {
        onNeedEmailVerification?.(formData.email);
      }
      return;
    }

    if (result.policyRules) {
      setPolicyRules(result.policyRules);
      toast(t('errors.passwordPolicyNotMet'), 'error');
      return;
    }

    if (result.fieldErrors) {
      const nextErrors: Record<string, string> = {};
      Object.entries(result.fieldErrors).forEach(([path, key]) => {
        const fieldKey = path === 'repeatPassword' ? 'confirmPassword' : path;
        nextErrors[fieldKey] = key;
      });
      setErrors(nextErrors);
      toast(t(nextErrors.email === 'errors.emailAlreadyRegistered' ? nextErrors.email : 'errors.registrationFailed'), 'error');
      return;
    }

    if (result.error) {
      toast(result.error, 'error');
    }
  };

  return (
    <AuthScreen>
      <div className="flex w-full flex-col gap-6">
        <div className="w-full space-y-3 text-center lg:text-left">
          <h2 className="auth-panel-title">{t('joinPaygatex')}</h2>
          <p className="auth-panel-description">{t('startAcceptingCrypto')}</p>
        </div>

        <RegistrationForm
          t={t}
          formData={formData}
          setFormData={handleFormChange}
          onFieldBlur={handleFieldBlur}
          errors={errors}
          policyRules={policyRules}
          setPolicyRules={setPolicyRules}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          showConfirmPassword={showConfirmPassword}
          setShowConfirmPassword={setShowConfirmPassword}
          isLoading={isLoading}
          onSubmit={handleSubmit}
          onSwitchToLogin={onSwitchToLogin}
        />
      </div>
    </AuthScreen>
  );
}
